import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import { complianceApi, type CompliancePreviewResponse, type CompliancePreviewRequest } from '../lib/api';
import StatesPicker from './lookup/StatesPicker';
import ChoiceList from './lookup/ChoiceList';
import ResultsView from './lookup/ResultsView';
import {
  ASSETS_OPTIONS,
  EMPLOYEE_OPTIONS,
  ENTITY_OPTIONS,
  INITIAL_RESPONSES,
  REVENUE_OPTIONS,
  STEPS,
  YES_NO_OPTIONS,
  type AssessmentResponses,
  type StepId,
} from './lookup/data';

function isStepAnswered(stepId: StepId, r: AssessmentResponses): boolean {
  switch (stepId) {
    case 'states':
      return r.states.length > 0;
    case 'foreignStates':
      return true;
    case 'entity':
      return !!r.entity;
    case 'revenue':
      return !!r.revenue;
    case 'assets':
      return !!r.assets;
    case 'sellsGoodsOrServices':
      return !!r.sellsGoodsOrServices;
    case 'hasPhysicalPresence':
      return !!r.hasPhysicalPresence;
    case 'hasEmployees':
      return r.hasEmployees.length > 0;
  }
}

export default function ComplianceLookup() {
  const [step, setStep] = useState(0);
  const [responses, setResponses] = useState<AssessmentResponses>(INITIAL_RESPONSES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState<CompliancePreviewResponse | null>(null);
  const [explained, setExplained] = useState(false);

  const buildPayload = (): CompliancePreviewRequest => ({
    entity: responses.entity,
    states: responses.states,
    foreignStates: responses.foreignStates,
    revenue: responses.revenue,
    assets: responses.assets,
    hasEmployees: responses.hasEmployees,
    sellsGoodsOrServices: responses.sellsGoodsOrServices || 'no',
    hasPhysicalPresence: responses.hasPhysicalPresence || 'no',
    hiresContractors: responses.hasEmployees.includes('contractors') ? 'yes' : 'no',
  });

  const handleExplain = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await complianceApi.preview(buildPayload(), { explain: true });
      setReport(res);
      setExplained(true);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error ?? err.message ?? 'Explain failed');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = () => {
    if (!report) return;
    const headers = ['category', 'priority', 'state', 'title', 'deadline', 'fee', 'description'];
    const escape = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
    const rows = report.filings.map((f) =>
      headers
        .map((h) => escape(String((f as unknown as Record<string, unknown>)[h] ?? '')))
        .join(',')
    );
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compliance-${responses.entity || 'report'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const current = STEPS[step];
  const canProceed = useMemo(() => isStepAnswered(current.id, responses), [current.id, responses]);
  const isLast = step === STEPS.length - 1;

  const update = <K extends keyof AssessmentResponses>(key: K, value: AssessmentResponses[K]) => {
    setResponses((prev) => {
      const next = { ...prev, [key]: value };
      return next;
    });
  };

  const handleNext = async () => {
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await complianceApi.preview(buildPayload());
      setReport(res);
      setExplained(false);
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err.response?.data?.error ?? err.message ?? 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setResponses(INITIAL_RESPONSES);
    setStep(0);
    setReport(null);
    setError('');
    setExplained(false);
  };

  if (report) {
    return (
      <ResultsView
        report={report}
        explained={explained}
        onRestart={handleReset}
        onExplain={handleExplain}
        onExportCsv={handleExportCsv}
      />
    );
  }

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700}>
          Compliance Lookup
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Walk through the assessment to surface federal, state, tax, license, and employment requirements.
        </Typography>
      </Box>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {STEPS.map((s, i) => (
          <Step key={s.id} completed={i < step}>
            <StepLabel>{s.shortLabel}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 4 }, mb: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          {current.title}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {current.subtitle}
        </Typography>

        {current.id === 'states' && (
          <StatesPicker value={responses.states} onChange={(v) => update('states', v)} />
        )}

        {current.id === 'foreignStates' && (
          <StatesPicker
            value={responses.foreignStates}
            onChange={(v) => update('foreignStates', v)}
          />
        )}

        {current.id === 'entity' && (
          <Stack spacing={3}>
            <ChoiceList
              options={ENTITY_OPTIONS}
              value={responses.entity}
              onChange={(v) => update('entity', v)}
            />
          </Stack>
        )}

        {current.id === 'revenue' && (
          <ChoiceList
            options={REVENUE_OPTIONS}
            value={responses.revenue}
            onChange={(v) => update('revenue', v)}
          />
        )}

        {current.id === 'assets' && (
          <ChoiceList
            options={ASSETS_OPTIONS}
            value={responses.assets}
            onChange={(v) => update('assets', v)}
          />
        )}

        {current.id === 'sellsGoodsOrServices' && (
          <ChoiceList
            options={YES_NO_OPTIONS}
            value={responses.sellsGoodsOrServices}
            onChange={(v) => update('sellsGoodsOrServices', v as 'yes' | 'no')}
          />
        )}

        {current.id === 'hasPhysicalPresence' && (
          <ChoiceList
            options={YES_NO_OPTIONS}
            value={responses.hasPhysicalPresence}
            onChange={(v) => update('hasPhysicalPresence', v as 'yes' | 'no')}
          />
        )}

        {current.id === 'hasEmployees' && (
          <ChoiceList
            multiple
            options={EMPLOYEE_OPTIONS}
            value={responses.hasEmployees}
            onChange={(v) => update('hasEmployees', v)}
          />
        )}
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Button
          startIcon={<ArrowBackIcon />}
          disabled={step === 0 || loading}
          onClick={() => setStep((s) => s - 1)}
        >
          Back
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {step > 0 && (
            <Button
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              disabled={loading}
            >
              Start over
            </Button>
          )}
          <Button
            variant="contained"
            endIcon={
              loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : isLast ? (
                <SearchIcon />
              ) : (
                <ArrowForwardIcon />
              )
            }
            disabled={!canProceed || loading}
            onClick={handleNext}
          >
            {isLast ? (loading ? 'Looking up…' : 'Run lookup') : 'Continue'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
